import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Target } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Separator } from '@/shared/components/ui/separator';
import type { CalendarEventModality } from '../types';

const MODALITY_OPTIONS: CalendarEventModality[] = ['virtual', 'in-person', 'hybrid'];

const MODALITY_COLOR: Record<CalendarEventModality, string> = {
  virtual: 'text-chart-2',
  'in-person': 'text-chart-3',
  hybrid: 'text-chart-4',
};

function ModalityIcon({
  modality,
  className,
}: {
  modality: CalendarEventModality | 'none';
  className?: string;
}) {
  const colorClass = modality === 'none' ? 'text-muted-foreground' : MODALITY_COLOR[modality];
  return <Target className={cn('size-3.5 shrink-0', colorClass, className)} aria-hidden />;
}

interface ModalityPickerProps {
  modality: CalendarEventModality | null;
  onSelect: (modality: CalendarEventModality | null) => void;
  disabled?: boolean;
}

/** Mirrors `PriorityPicker` (Popover + colored-text trigger, "active" then "all" sections). */
export function ModalityPicker({ modality, onSelect, disabled }: ModalityPickerProps) {
  const { t } = useTranslation('calendar');
  const [open, setOpen] = useState(false);

  function handleSelect(next: CalendarEventModality | null) {
    onSelect(next);
    setOpen(false);
  }

  const allOptions = modality ? MODALITY_OPTIONS.filter((m) => m !== modality) : MODALITY_OPTIONS;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          data-testid="calendar-modality-picker-trigger"
          className="flex items-center gap-1.5 text-xs transition-opacity hover:opacity-70"
          onClick={(e) => e.stopPropagation()}
        >
          <ModalityIcon modality={modality ?? 'none'} />
          <span className={modality ? MODALITY_COLOR[modality] : 'text-muted-foreground'}>
            {modality
              ? t(`eventFicha.modality.options.${modality}`)
              : t('eventFicha.modality.none')}
          </span>
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className="w-48 p-0"
        data-testid="calendar-modality-picker-popover"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-3 py-2 text-xs font-semibold">{t('eventFicha.modality.label')}</div>
        <Separator />

        {modality && (
          <>
            <div className="px-3 pt-2 pb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
              {t('eventFicha.modality.activeTitle')}
            </div>
            <div data-testid="calendar-modality-picker-active-section" className="px-1 pb-1">
              <button
                type="button"
                onClick={() => handleSelect(null)}
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs bg-accent/40 hover:bg-accent"
              >
                <ModalityIcon modality={modality} />
                <span className={MODALITY_COLOR[modality]}>
                  {t(`eventFicha.modality.options.${modality}`)}
                </span>
              </button>
            </div>
            <Separator />
          </>
        )}

        <div className="px-3 pt-2 pb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
          {t('eventFicha.modality.allTitle')}
        </div>
        <div data-testid="calendar-modality-picker-all-section" className="px-1 pb-1">
          {allOptions.map((option) => (
            <button
              type="button"
              key={option}
              data-testid={`calendar-modality-option-${option}`}
              onClick={() => handleSelect(option)}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-accent"
            >
              <ModalityIcon modality={option} />
              <span className={MODALITY_COLOR[option]}>
                {t(`eventFicha.modality.options.${option}`)}
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
